import { computed, ref } from 'vue'

import type { ComputedRef, Ref } from 'vue'
import type { NativeSelectMode, NativeSelectOption } from '@shared/ui/fields'

import { useListsStore } from '@shared/model'

import { useTutorKanbanStore } from './store'
import { useTutorKanbanExitConfirm } from './tutor-kanban-exit-confirm.hook'
import { getTutorStageCategories } from '../config/tutor-kanban-stage-category-key.const'
import { isTutorKanbanRequiredCategoryStage } from '../config/tutor-kanban-exit-stage.const'

interface TutorKanbanSelectCategoryReturn {
  isVisible: ComputedRef<boolean>
  isRequired: ComputedRef<boolean>
  refOptions: ComputedRef<NativeSelectOption[]>
  refValue: ComputedRef<string | null>
  refMode: ComputedRef<NativeSelectMode>
  refResetKey: Ref<number>
  isConfirmOpen: Ref<boolean>
  setChange: (option: NativeSelectOption) => void
  setConfirmChange: () => void
  setCancelChange: () => void
}

export const useTutorKanbanSelectCategory = (): TutorKanbanSelectCategoryReturn => {
  const tutorKanbanStore = useTutorKanbanStore()
  const listsStore = useListsStore()
  const { isCategoryConfirmRequired } = useTutorKanbanExitConfirm()

  const refPendingValue = ref<string | null>(null)
  const isConfirmOpen = ref<boolean>(false)
  const refResetKey = ref<number>(0)

  const refStageCategories = computed<Record<string, string>>(() => {
    return getTutorStageCategories(tutorKanbanStore.getStage, listsStore.getLists?.tutor_stage_category)
  })

  const isVisible = computed<boolean>(() => Object.keys(refStageCategories.value).length > 0)

  const isRequired = computed<boolean>(() => {
    return isVisible.value && isTutorKanbanRequiredCategoryStage(tutorKanbanStore.getStage)
  })

  const refValue = computed<string | null>(() => {
    const value = tutorKanbanStore.getTutor?.stage_details?.categories?.[0] ?? null
    if (!value) return null

    return refStageCategories.value[value] ? value : null
  })

  const refOptions = computed<NativeSelectOption[]>(() => {
    const current = refValue.value
    const options: NativeSelectOption[] = Object.entries(refStageCategories.value).map(([value, text]) => ({
      id: value,
      value,
      text,
      selected: current === value,
    }))

    return options
  })

  const refMode = computed<NativeSelectMode>(() => {
    if (tutorKanbanStore.isValid) return 'normal'
    if (isRequired.value && !refValue.value) return 'error'
    return 'normal'
  })

  const setCategory = (value: string | null): void => {
    tutorKanbanStore.setTutorStageCategories(value ? [value] : [])
  }

  const setChange = (option: NativeSelectOption): void => {
    const value = option.value === null ? null : String(option.value)

    if (value === refValue.value) return

    if (isCategoryConfirmRequired.value) {
      refPendingValue.value = value
      isConfirmOpen.value = true
      return
    }

    setCategory(value)
  }

  const setConfirmChange = (): void => {
    setCategory(refPendingValue.value)
    refPendingValue.value = null
    isConfirmOpen.value = false
  }

  const setCancelChange = (): void => {
    refPendingValue.value = null
    isConfirmOpen.value = false
    refResetKey.value += 1
  }

  return {
    isVisible,
    isRequired,
    refOptions,
    refValue,
    refMode,
    refResetKey,
    isConfirmOpen,
    setChange,
    setConfirmChange,
    setCancelChange,
  }
}
